import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_BASE_URL from '../api';

const CareerInfo = () => {
  const [careerInfo, setCareerInfo] = useState({
    careerField: '',
    otherCareerField: '', // To store the value if "Other" is selected
    careerTitle: '',
    similarField: '',
    school: '',
    similarEducationLevel: '',
  });

  const [message, setMessage] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const navigate = useNavigate();

  const predefinedCareerFields = [
    'Engineering & Architecture',
    'Arts & Entertainment',
    'Administration, business & management',
    'Communications',
    'Community & Social Services',
    'Education',
    'Science & Technology',
    'Installation, Repair, and Maintenance',
    'Farming, Fishing, & Forestry',
    'Government',
    'Health and Medicine',
    'Law & Public Policy',
    'Sales',
    'Other',
  ];

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_BASE_URL}/api/career-info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => {
        if (response.status === 404) {
          setIsNewUser(true);
          setMessage('No career info found. Please set your career info.');
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to fetch career info');
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          const careerData = data.reduce((acc, item) => {
            if (item.question_text === 'What is your career field?') {
              acc.careerField = predefinedCareerFields.includes(
                item.answer_text,
              )
                ? item.answer_text
                : 'Other';
              acc.otherCareerField = predefinedCareerFields.includes(
                item.answer_text,
              )
                ? ''
                : item.answer_text;
            }
            if (item.question_text === 'What is your career job/title?')
              acc.careerTitle = item.answer_text;
            if (
              item.question_text ===
              'Are you looking for someone in a similar field?'
            )
              acc.similarField = item.answer_text;
            if (
              item.question_text ===
              'What school did you attend/are currently attending?'
            )
              acc.school = item.answer_text;
            if (
              item.question_text ===
              'Are you looking for someone with a similar education level?'
            )
              acc.similarEducationLevel = item.answer_text;
            return acc;
          }, {});
          setCareerInfo(careerData);
        }
      })
      .catch((error) => {
        console.error('Error fetching career info:', error);
        setMessage('Error fetching career info.');
      });
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = getAuthToken();

    const payload = {
      careerField: careerInfo.careerField,
      otherCareerField:
        careerInfo.careerField === 'Other' ? careerInfo.otherCareerField : '',
      careerTitle: careerInfo.careerTitle,
      similarField: careerInfo.similarField,
      school: careerInfo.school,
      similarEducationLevel: careerInfo.similarEducationLevel,
    };

    const method = isNewUser ? 'POST' : 'PUT';

    try {
      const response = await fetch(`${API_BASE_URL}/api/career-info`, {
        method: method,
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update career info');
      }

      setCareerInfo(payload); // Set state to the values that were just submitted
      setMessage('Career info updated successfully.');

      setTimeout(() => {
        navigate('/political-info');
      }, 2000);

      setIsNewUser(false);
    } catch (error) {
      console.error('Error setting career info:', error);
      setMessage('Failed to update career info.');
    }
  };

  const handleChange = (e) => {
    setCareerInfo({
      ...careerInfo,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0D2329' }}
    >
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div
          className="w-full max-w-md p-8 rounded-lg shadow-md"
          style={{ backgroundColor: '#05664f' }}
        >
          <h1 className="text-4xl font-bold text-white text-center mb-6">
            Career Info
          </h1>
          {message && (
            <p className="text-sm text-red-200 text-center mb-4">{message}</p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-white">
              <label className="block text-sm font-medium">
                What is your career field?
              </label>
              <select
                name="careerField"
                value={careerInfo.careerField || ''}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black sm:text-sm rounded-md"
                required
              >
                <option value="">Select your career field</option>
                {predefinedCareerFields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
              {careerInfo.careerField === 'Other' && (
                <div className="mt-2">
                  <label className="block text-sm font-medium">
                    Please specify your career field:
                    <input
                      type="text"
                      name="otherCareerField"
                      value={careerInfo.otherCareerField || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black sm:text-sm rounded-md"
                      required
                    />
                  </label>
                </div>
              )}
            </div>
            <div className="text-white">
              <label className="block text-sm font-medium">
                What is your career job/title?
                <input
                  type="text"
                  name="careerTitle"
                  value={careerInfo.careerTitle || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black sm:text-sm rounded-md"
                  required
                />
              </label>
            </div>

            {/* Square buttons for "Are you looking for someone in a similar field?" */}
            <div className="text-white">
              <label className="block text-sm font-medium">
                Are you looking for someone in a similar field?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarField"
                    value="Yes"
                    checked={careerInfo.similarField === 'Yes'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarField"
                    value="Maybe"
                    checked={careerInfo.similarField === 'Maybe'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Maybe</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarField"
                    value="No"
                    checked={careerInfo.similarField === 'No'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <div className="text-white">
              <label className="block text-sm font-medium">
                What school did you attend/are currently attending?
                <input
                  type="text"
                  name="school"
                  value={careerInfo.school || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black sm:text-sm rounded-md"
                  required
                />
              </label>
            </div>

            {/* Square buttons for "Are you looking for someone with a similar education level?" */}
            <div className="text-white">
              <label className="block text-sm font-medium">
                Are you looking for someone with a similar education level?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarEducationLevel"
                    value="Yes"
                    checked={careerInfo.similarEducationLevel === 'Yes'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarEducationLevel"
                    value="Maybe"
                    checked={careerInfo.similarEducationLevel === 'Maybe'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Maybe</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="similarEducationLevel"
                    value="No"
                    checked={careerInfo.similarEducationLevel === 'No'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>

            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-2"
              onClick={() => navigate('/seeking-info')} // Navigate back to the previous form
            >
              Previous
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CareerInfo;
